// TODO(brand): migrate to <Button>
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { GameEvent, Team, ViewState, MatchRecord, Organization, StatRecord, isGameType, Season } from '../types';
import { Calendar, MapPin, Clock, Plus, X, Upload, Loader2, Sparkles, Trash2, Check, Edit2, AlertTriangle, ChevronDown, History, Filter, Star, ChevronUp, BarChart2, Trophy, CalendarDays } from 'lucide-react';
import { extractScheduleFromFile } from '../services/geminiService';
import { supabase, mapEventFromDB, setMatchOfRecord, withAutoRefresh, mapSeasonFromDB } from '../services/supabase';
import SeasonSelector from './SeasonSelector';
import { formatDate, formatMonth, formatMonthDay } from '../lib/dates';

interface ScheduleProps {
  organizationId: string;
  currentOrg?: Organization;
  teams: Team[];
  events: GameEvent[];
  matchRecords?: MatchRecord[];
  setMatchRecords?: React.Dispatch<React.SetStateAction<MatchRecord[]>>;
  setEvents: React.Dispatch<React.SetStateAction<GameEvent[]>>;
  changeView: (view: ViewState) => void;
  onViewStats?: (eventId: string) => void;
  onPlayGame?: (matchId: string) => void;
  onOpenPractice?: (date: string) => void;
  activeTeamId?: string;
  seasons?: Season[];
  setSeasons?: React.Dispatch<React.SetStateAction<Season[]>>;
}

const Schedule: React.FC<ScheduleProps> = ({ organizationId, currentOrg, teams, events, matchRecords = [], setMatchRecords, setEvents, changeView, onViewStats, onPlayGame, onOpenPractice, activeTeamId, seasons = [], setSeasons }) => {
  const [filterTeamId, setFilterTeamId] = useState<string>('');
  const hasInitializedTeamFilter = useRef(false);
  // Only show own (non-opponent) teams in the filter/form
  const ownTeams = useMemo(() => teams.filter(t => t.type !== 'opponent'), [teams]);

  // Sync filter team with the global active team
  useEffect(() => {
    if (!activeTeamId) return;
    setFilterTeamId(activeTeamId);
    hasInitializedTeamFilter.current = true;
  }, [activeTeamId]);

  // Parse set result from event title fallback (e.g. "vs Team (W 3-1)")
  const parseResultFromTitle = (evt: GameEvent): 'W' | 'L' | null => {
    const m = evt.title?.match(/\(([WL])\s+\d+-\d+\)/i);
    return m ? (m[1].toUpperCase() as 'W' | 'L') : null;
  };

  const [viewingSeason, setViewingSeason] = useState<Season | null>(null);

  // Season bounds: use selected season's dates, or active season's dates, or default Aug 1 – Jul 31
  const currentSeasonBounds = useMemo(() => {
    const target = viewingSeason ?? seasons.find(s => s.isActive) ?? null;
    if (target?.startDate && target?.endDate) {
      return { start: target.startDate, end: target.endDate };
    }
    if (target?.startDate) {
      // Open-ended season — end 1 year after start
      const startYear = parseInt(target.startDate.slice(0, 4));
      return { start: target.startDate, end: `${startYear + 1}-07-31` };
    }
    // Fallback: hardcoded Aug 1 – Jul 31
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const startYear = m >= 8 ? y : y - 1;
    return { start: `${startYear}-08-01`, end: `${startYear + 1}-07-31` };
  }, [viewingSeason, seasons]);

  // Team W-L record — current season only, scoped to filter team if set
  const teamRecord = useMemo(() => {
    const opponentTeamIds = new Set(teams.filter(t => t.type === 'opponent').map(t => t.id));
    let wins = 0, losses = 0;
    events.forEach(evt => {
      if (!isGameType(evt.type)) return;
      if (!evt.date || evt.date < currentSeasonBounds.start || evt.date > currentSeasonBounds.end) return;
      // Exclude events explicitly tied to an opponent team
      if (evt.teamId && opponentTeamIds.has(evt.teamId)) return;
      if (filterTeamId && evt.teamId && evt.teamId !== filterTeamId) return;
      // Determine result: match record sets first, then title fallback
      const record = matchRecords.find(r => r.eventId === evt.id && r.isRecord);
      let result: 'W' | 'L' | null = null;
      if (record) {
        const { ourSetsWon, theirSetsWon } = record.gameState;
        if (ourSetsWon > theirSetsWon) result = 'W';
        else if (theirSetsWon > ourSetsWon) result = 'L';
      }
      if (!result) result = parseResultFromTitle(evt);
      if (result === 'W') wins++;
      else if (result === 'L') losses++;
    });
    return { wins, losses };
  }, [events, matchRecords, teams, filterTeamId, currentSeasonBounds]);
  // Match results panel state
  const [expandedResultsEventId, setExpandedResultsEventId] = useState<string | null>(null);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<Record<string, string>>({}); // eventId → recordId
  const [settingRecord, setSettingRecord] = useState<string | null>(null); // recordId being set
  const isAdmin = currentOrg?.isAdmin ?? false;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newEvent, setNewEvent] = useState<Partial<GameEvent>>({ type: 'practice' });
  
  // Custom Time State
  const [timeHour, setTimeHour] = useState('12');
  const [timeMinute, setTimeMinute] = useState('00');
  const [timeAmPm, setTimeAmPm] = useState('PM');
  const [isTbdTime, setIsTbdTime] = useState(false);

  // End Time State
  const [hasEndTime, setHasEndTime] = useState(false);
  const [endTimeHour, setEndTimeHour] = useState('6');
  const [endTimeMinute, setEndTimeMinute] = useState('00');
  const [endTimeAmPm, setEndTimeAmPm] = useState('PM');

  // Tournament state
  const [isTournament, setIsTournament] = useState(false);
  const [tournamentName, setTournamentName] = useState('');
  const [tournamentId, setTournamentId] = useState('');

  const [isExtracting, setIsExtracting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ id: string, title: string, tournamentId?: string } | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Review Mode State
  const [isReviewing, setIsReviewing] = useState(false);
  const [scannedEvents, setScannedEvents] = useState<Partial<GameEvent>[]>([]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Robust Date Parser to handle mixed formats (YYYY-MM-DD vs MM/DD/YYYY)
  const robustParseDate = (dateStr?: string): Date | null => {
    if (!dateStr) return null;
    
    // 1. Try ISO YYYY-MM-DD (Manual Entry standard)
    const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (isoMatch) {
        return new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
    }

    // 2. Try US MM/DD/YYYY (Common extracted format)
    const usMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usMatch) {
        return new Date(parseInt(usMatch[3]), parseInt(usMatch[1]) - 1, parseInt(usMatch[2]));
    }

    // 3. Fallback to native parsing, then normalize to midnight local
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  // Helper to parse existing time string into picker state
  const parseTimeStringToState = (timeStr?: string) => {
    if (!timeStr || timeStr === 'TBD') {
        setTimeHour('5');
        setTimeMinute('00');
        setTimeAmPm('PM');
        setIsTbdTime(true);
        return;
    }

    setIsTbdTime(false);
    
    // Check for "HH:MM AM/PM" format
    const amPmMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (amPmMatch) {
        setTimeHour(amPmMatch[1]);
        setTimeMinute(amPmMatch[2]);
        setTimeAmPm(amPmMatch[3].toUpperCase());
        return;
    }

    // Check for "HH:MM" 24h format
    const match24 = timeStr.match(/(\d{1,2}):(\d{2})/);
    if (match24) {
        let h = parseInt(match24[1]);
        const m = match24[2];
        const ampm = h >= 12 ? 'PM' : 'AM';
        if (h > 12) h -= 12;
        if (h === 0) h = 12;
        setTimeHour(h.toString());
        setTimeMinute(m);
        setTimeAmPm(ampm);
        return;
    }
  };

  const getTimeMinutes = (timeStr?: string) => {
    if (!timeStr || timeStr === 'TBD') return 1440; // Sort TBD to end of day
    
    try {
        const lower = timeStr.toLowerCase();
        let clean = lower.replace(/\s+/g, ' ').trim();
        
        let hours = 0;
        let minutes = 0;
        let isPm = clean.includes('pm');
        let isAm = clean.includes('am');

        const timeMatch = clean.match(/(\d{1,2}):(\d{2})/);
        if (timeMatch) {
            hours = parseInt(timeMatch[1]);
            minutes = parseInt(timeMatch[2]);
        }

        if (isPm && hours < 12) hours += 12;
        if (isAm && hours === 12) hours = 0;
        
        return hours * 60 + minutes;
    } catch (e) {
        return 1440;
    }
  };

  // Split events into Upcoming and Past, applying team filter
  const { upcomingEvents, pastEvents } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcoming: GameEvent[] = [];
    const past: GameEvent[] = [];

    const filtered = filterTeamId
      ? events.filter(evt => !evt.teamId || evt.teamId === filterTeamId)
      : events;

    filtered.forEach(evt => {
        const d = robustParseDate(evt.date);
        
        // If date is invalid or missing, default to upcoming (end of list)
        if (!d) {
            upcoming.push(evt);
            return;
        }

        if (d < today) {
            past.push(evt);
        } else {
            upcoming.push(evt);
        }
    });

    // Sort Upcoming: ASC (Earliest date first)
    upcoming.sort((a, b) => {
        const da = robustParseDate(a.date);
        const db = robustParseDate(b.date);
        
        // Put invalid dates at the very end
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;

        const diff = da.getTime() - db.getTime();
        if (diff !== 0) return diff;
        return getTimeMinutes(a.time) - getTimeMinutes(b.time);
    });

    // Sort Past: DESC (Most recent past event first)
    past.sort((a, b) => {
        const da = robustParseDate(a.date)!;
        const db = robustParseDate(b.date)!;
        
        const diff = db.getTime() - da.getTime();
        if (diff !== 0) return diff;
        return getTimeMinutes(b.time) - getTimeMinutes(a.time);
    });

    return { upcomingEvents: upcoming, pastEvents: past };
  }, [events, filterTeamId]);

  // Returns today's date as YYYY-MM-DD in LOCAL time (avoids UTC day-shift bug)
  const localTodayStr = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const openAddModal = (prefill?: Partial<GameEvent> | React.MouseEvent | React.UIEvent) => {
    // Prevent React MouseEvents from leaking into the event state
    const isEventObj = prefill && ('nativeEvent' in prefill || 'clientX' in prefill || ('type' in prefill && (prefill as any).type === 'click'));
    const safePrefill = isEventObj ? {} : (prefill as Partial<GameEvent> || {});

    setEditingId(null);
    // Strip undefined values from prefill so they don't accidentally override defaults like localTodayStr()
    const cleanPrefill = Object.fromEntries(
      Object.entries(safePrefill).filter(([, v]) => v !== undefined)
    ) as Partial<GameEvent>;
    setNewEvent({
        type: 'practice',
        date: localTodayStr(),
        teamId: filterTeamId || undefined,
        ...cleanPrefill,
    });
    parseTimeStringToState(safePrefill.time || '5:00 PM');
    setHasEndTime(false);
    setEndTimeHour('6');
    setEndTimeMinute('00');
    setEndTimeAmPm('PM');
    setIsTournament(!!(safePrefill?.tournamentId));
    setTournamentName(safePrefill?.tournamentName || '');
    setTournamentId(safePrefill?.tournamentId || '');
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (event: GameEvent) => {
    setEditingId(event.id);

    // Sanitize invalid types (like "click") that might be lingering in the database
    const validTypes = ['practice', 'game', 'tournament', 'other'];
    const safeType = validTypes.includes(event.type) ? event.type : 'practice';

    setNewEvent({ ...event, type: safeType as GameEvent['type'] });
    parseTimeStringToState(event.time);
    if (event.endTime) {
      setHasEndTime(true);
      const amPmMatch = event.endTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (amPmMatch) {
        setEndTimeHour(amPmMatch[1]);
        setEndTimeMinute(amPmMatch[2]);
        setEndTimeAmPm(amPmMatch[3].toUpperCase());
      }
    } else {
      setHasEndTime(false);
      setEndTimeHour('6');
      setEndTimeMinute('00');
      setEndTimeAmPm('PM');
    }
    setIsTournament(!!event.tournamentId);
    setTournamentName(event.tournamentName || '');
    setTournamentId(event.tournamentId || '');
    setFormErrors({});
    setIsModalOpen(true);
  };

  const initiateDeleteEvent = (event: GameEvent) => {
    setDeleteConfirmation({ id: event.id, title: event.title });
  };

  const executeDeleteEvent = async () => {
    if (!deleteConfirmation) return;
    const { id, tournamentId } = deleteConfirmation;

    if (tournamentId) {
      // Delete all events belonging to this tournament
      const idsToDelete = events.filter(e => e.tournamentId === tournamentId).map(e => e.id);
      setEvents(prev => prev.filter(e => e.tournamentId !== tournamentId));
      setDeleteConfirmation(null);
      if (organizationId !== 'dev-org-id') {
        try {
          await supabase.from('events').delete().in('id', idsToDelete);
        } catch (error) {
          console.error("Error deleting tournament events", error);
        }
      }
      return;
    }

    // Optimistic Update
    setEvents(prev => prev.filter(e => e.id !== id));
    setDeleteConfirmation(null);

    if (organizationId === 'dev-org-id') return;

    try {
      await supabase.from('events').delete().eq('id', id);
    } catch (error) {
      console.error("Error deleting event", error);
    }
  };

  const handleSaveEvent = async () => {
    const eventType = newEvent.type || 'practice';
    // Validation
    const errors: Record<string, string> = {};
    if (eventType === 'game') {
      if (!newEvent.opponent?.trim()) errors.opponent = 'Opponent name is required';
    } else if (eventType === 'tournament') {
      if (!newEvent.title?.trim()) errors.title = 'Tournament name is required';
    } else if (eventType === 'other') {
      if (!newEvent.title?.trim()) errors.title = 'Title is required';
    }
    if (!newEvent.date) errors.date = 'Date is required';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});

    // Construct time string from picker
    const finalTime = isTbdTime ? 'TBD' : `${timeHour}:${timeMinute} ${timeAmPm}`;
    const finalEndTime = hasEndTime ? `${endTimeHour}:${endTimeMinute} ${endTimeAmPm}` : undefined;

    // For tournament type, the title field IS the tournament name
    const isTournamentType = eventType === 'tournament';
    const finalTournamentId = isTournamentType
      ? (tournamentId || crypto.randomUUID())
      : isTournament ? (tournamentId || crypto.randomUUID()) : undefined;
    const finalTournamentName = isTournamentType
      ? newEvent.title
      : isTournament ? tournamentName : undefined;

    // Auto-derive title for game and practice; require title for other/tournament
    const teamName = ownTeams.find(t => t.id === newEvent.teamId)?.name;
    const derivedTitle = eventType === 'game'
      ? (newEvent.title?.trim() || (teamName ? `${teamName} Game` : 'Game'))
      : eventType === 'practice'
        ? (newEvent.title?.trim() || (teamName ? `${teamName} Practice` : 'Practice'))
        : (newEvent.title || '');

    const eventData = {
      type: eventType,
      title: derivedTitle,
      date: newEvent.date!,
      time: finalTime,
      endTime: finalEndTime,
      location: newEvent.location || 'TBD',
      opponent: newEvent.opponent,
      teamId: newEvent.teamId || undefined,
      tournamentId: finalTournamentId,
      tournamentName: finalTournamentName,
    };

    // Snake_case payload for Supabase — never include camelCase keys or the schema cache throws
    const dbPayload = {
      type: eventData.type,
      title: eventData.title || (eventType === 'practice' ? 'Practice' : eventType === 'game' ? 'Game' : 'Untitled'),
      date: eventData.date,
      time: eventData.time,
      end_time: eventData.endTime || null,
      location: eventData.location,
      opponent: eventData.opponent || null,
      team_id: eventData.teamId || null,
      tournament_id: eventData.tournamentId || null,
      tournament_name: eventData.tournamentName || null,
    };

    // DEV BYPASS
    if (organizationId === 'dev-org-id') {
      if (editingId) {
        setEvents(prev => prev.map(e => e.id === editingId ? { ...e, ...eventData } : e));
      } else {
        const event: GameEvent = {
            id: `dev-event-${Date.now()}`,
            organizationId,
            ...eventData
        } as GameEvent;
        setEvents(prev => [...prev, event]);
      }
      setIsModalOpen(false);
      setNewEvent({ type: 'practice' });
      setEditingId(null);
      return;
    }

    if (editingId) {
      // UPDATE — optimistic update first, then sync with DB
      setEvents(prev => prev.map(e => e.id === editingId ? { ...e, ...eventData, id: editingId, organizationId, stats: e.stats || [] } : e));
      setIsModalOpen(false);
      setNewEvent({ type: 'practice' });
      setEditingId(null);

      try {
        const isEndTimeSchemaError = (e: any) =>
          e?.message?.includes('end_time') && e?.message?.includes('schema cache');
        const { end_time: _et, ...dbPayloadWithoutEndTime } = dbPayload;

        let { data, error } = await withAutoRefresh(() =>
          supabase.from('events').update(dbPayload).eq('id', editingId).select()
        );
        if (error && isEndTimeSchemaError(error)) {
          ({ data, error } = await withAutoRefresh(() =>
            supabase.from('events').update(dbPayloadWithoutEndTime).eq('id', editingId).select()
          ));
        }

        if (data && !error) {
          // Replace with the authoritative DB version
          const updatedEvent = mapEventFromDB(data[0]);
          setEvents(prev => prev.map(e => e.id === editingId ? updatedEvent : e));
        }
      } catch (error) {
        console.error("Error updating event", error);
      }
    } else {
      // INSERT — optimistic update: add immediately with a temp ID, close modal, then swap in real ID
      const tempId = `temp-${Date.now()}`;
      const optimisticEvent: GameEvent = { id: tempId, organizationId, ...eventData, stats: [] } as GameEvent;
      setEvents(prev => [...prev, optimisticEvent]);
      setIsModalOpen(false);
      setNewEvent({ type: 'practice' });
      setEditingId(null);

      try {
        const isEndTimeSchemaError = (e: any) =>
          e?.message?.includes('end_time') && e?.message?.includes('schema cache');
        const { end_time: _et, ...dbPayloadWithoutEndTime } = dbPayload;

        // Insert with .select() so we can swap the temp ID for the real DB UUID
        let { data: insertData, error: insertError } = await withAutoRefresh(() =>
          supabase.from('events').insert([{ ...dbPayload, organization_id: organizationId }]).select()
        );
        if (insertError && isEndTimeSchemaError(insertError)) {
          ({ data: insertData, error: insertError } = await withAutoRefresh(() =>
            supabase.from('events').insert([{ ...dbPayloadWithoutEndTime, organization_id: organizationId }]).select()
          ));
        }

        if (insertError) {
          // Genuine insert failure — rollback and alert
          console.error("Error inserting event", insertError);
          setEvents(prev => prev.filter(e => e.id !== tempId));
          alert(`Failed to save event: ${insertError.message}`);
        } else if (insertData && insertData[0]) {
          // Swap temp ID for the real DB event (includes real UUID)
          const savedEvent = mapEventFromDB(insertData[0]);
          setEvents(prev => prev.map(e => e.id === tempId ? savedEvent : e));
        }
      } catch (error) {
        console.error("Error inserting event", error);
        setEvents(prev => prev.filter(e => e.id !== tempId));
      }
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsExtracting(true);

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const base64String = reader.result as string;
        const extractedEvents = await extractScheduleFromFile(base64String);

        if (extractedEvents.length > 0) {
          const identifiedEvents = extractedEvents.map((evt, idx) => ({
            ...evt,
            id: `scan-${Date.now()}-${idx}`
          }));
          setScannedEvents(identifiedEvents);
          setIsReviewing(true);
        } else {
          alert("No events were found in the file. Make sure the image or PDF shows a schedule and try again.");
        }
      } catch (error: any) {
        console.error("Upload failed:", error);
        const isNetworkError = error instanceof TypeError && error.message.includes('fetch');
        alert(isNetworkError
          ? "Could not reach the AI service. Make sure the API server is running (npm run server)."
          : `Failed to extract schedule: ${error.message ?? 'Unknown error'}`
        );
      } finally {
        setIsExtracting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const updateScannedEvent = (id: string, field: keyof GameEvent, value: string) => {
    setScannedEvents(prev => prev.map(evt => 
      evt.id === id ? { ...evt, [field]: value } : evt
    ));
  };

  const removeScannedEvent = (id: string) => {
    setScannedEvents(prev => prev.filter(evt => evt.id !== id));
  };

  const commitScannedEvents = async () => {
    const missingOpponents = scannedEvents.filter(
      evt => (evt.type === 'game' || evt.type === 'tournament') && !evt.opponent?.trim()
    );
    if (missingOpponents.length > 0) return;

    // DEV BYPASS
    if (organizationId === 'dev-org-id') {
      const newEvents = scannedEvents.map((evt, idx) => ({
        id: `dev-scan-${Date.now()}-${idx}`,
        organizationId,
        type: (evt.type as GameEvent['type']) || 'game',
        title: evt.title || 'Untitled Event',
        date: evt.date || new Date().toISOString().split('T')[0],
        time: evt.time || 'TBD',
        location: evt.location || 'TBD',
        opponent: evt.opponent
      }));
      setEvents(prev => [...prev, ...newEvents]);
      setIsReviewing(false);
      setScannedEvents([]);
      return;
    }

    const validEvents = scannedEvents.map((evt) => ({
      organization_id: organizationId,
      type: (evt.type as GameEvent['type']) || 'game',
      title: evt.title || 'Untitled Event',
      date: evt.date || new Date().toISOString().split('T')[0],
      time: evt.time || 'TBD',
      location: evt.location || 'TBD',
      opponent: evt.opponent
    }));

    try {
      const { data, error } = await supabase
        .from('events')
        .insert(validEvents)
        .select();

      if (data && !error) {
        const newEvents = data.map(mapEventFromDB);
        setEvents(prev => [...prev, ...newEvents]);
        setIsReviewing(false);
        setScannedEvents([]);
      }
    } catch (error) {
       console.error("Error bulk adding events", error);
    }
  };

  const handleViewDetails = (event: GameEvent) => {
    const eventRecords = matchRecords.filter(r => r.eventId === event.id);
    if (isGameType(event.type) && eventRecords.length > 0) {
      // Toggle inline results panel
      setExpandedResultsEventId(prev => prev === event.id ? null : event.id);
      // Default to the record submission, or first
      if (!selectedSubmissionId[event.id]) {
        const record = eventRecords.find(r => r.isRecord) || eventRecords[0];
        setSelectedSubmissionId(prev => ({ ...prev, [event.id]: record.id }));
      }
    } else if (onViewStats) {
      onViewStats(event.id);
    } else {
      changeView(ViewState.STATS);
    }
  };

  const handleSetRecord = async (record: MatchRecord) => {
    if (!setMatchRecords) return;
    setSettingRecord(record.id);
    try {
      await setMatchOfRecord(record.id, record.eventId);
      setMatchRecords(prev => prev.map(r =>
        r.eventId === record.eventId ? { ...r, isRecord: r.id === record.id } : r
      ));
    } catch (e) {
      console.error('Failed to set match of record:', e);
    } finally {
      setSettingRecord(null);
    }
  };

  // Build per-player stat counts from a stats array
  const buildStatCounts = (stats: StatRecord[]) => {
    const counts: Record<string, Record<string, number>> = {};
    for (const s of stats) {
      if (!counts[s.playerId]) counts[s.playerId] = {};
      counts[s.playerId][s.statType] = (counts[s.playerId][s.statType] || 0) + 1;
    }
    return counts;
  };

  const KEY_STATS: StatRecord['statType'][] = ['kill', 'attack_error', 'ace', 'serve_error', 'dig', 'assist', 'block_solo', 'block_assist', 'reception', 'reception_error'];
  const STAT_LABELS: Partial<Record<StatRecord['statType'], string>> = {
    kill: 'K', attack_error: 'E', ace: 'Ace', serve_error: 'SE', dig: 'D',
    assist: 'A', block_solo: 'BS', block_assist: 'BA', reception: 'R', reception_error: 'RE',
  };

  const renderMatchResultsPanel = (event: GameEvent) => {
    const records = matchRecords.filter(r => r.eventId === event.id);
    if (records.length === 0) return null;
    const activeId = selectedSubmissionId[event.id] || (records.find(r => r.isRecord) || records[0]).id;
    const activeRecord = records.find(r => r.id === activeId) || records[0];
    const { gameState } = activeRecord;

    // All player IDs across all records for diff table
    const allPlayerIds = Array.from(new Set(records.flatMap(r => r.gameState.stats.map(s => s.playerId))));
    const allStatCounts = records.map(r => buildStatCounts(r.gameState.stats));

    return (
      <div className="mt-3 border-t border-slate-200 pt-3 space-y-3" onClick={e => e.stopPropagation()}>
        {/* Submission picker */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Viewing:</span>
          <select
            value={activeId}
            onChange={e => setSelectedSubmissionId(prev => ({ ...prev, [event.id]: e.target.value }))}
            className="text-sm font-semibold text-slate-800 border border-slate-200 rounded-lg px-2 py-1 bg-white outline-none"
          >
            {records.map(r => {
              const src = getRecordSource(r);
              const srcLabel = src === 'import' ? ' · Import' : src === 'manual' ? ' · Manual' : ' · Live';
              return (
                <option key={r.id} value={r.id}>
                  {r.submittedByName}{srcLabel}{r.isRecord ? ' ★' : ''}
                </option>
              );
            })}
          </select>
          {onViewStats && (
            <button
              onClick={() => onViewStats(event.id)}
              className="ml-auto text-xs text-blue-600 font-bold flex items-center gap-1 hover:underline"
            >
              <BarChart2 className="w-3 h-3" /> Full Stats
            </button>
          )}
        </div>

        {/* Score summary */}
        {(() => {
          let ourSets = gameState.ourSetsWon;
          let theirSets = gameState.theirSetsWon;
          // Fall back to title-parsed score when live set data is absent
          if (ourSets === 0 && theirSets === 0) {
            const m = event.title?.match(/\(([WL])\s+(\d+)-(\d+)\)/i);
            if (m) {
              const won = m[1].toUpperCase() === 'W';
              const a = parseInt(m[2]), b = parseInt(m[3]);
              ourSets = won ? a : b;
              theirSets = won ? b : a;
            }
          }
          const weWon = ourSets > theirSets;
          const theyWon = theirSets > ourSets;
          return (
            <div className="flex items-center gap-4 bg-slate-50 rounded-lg px-3 py-2 text-sm">
              <div className="flex-1 text-center">
                <div className={`font-black text-xl ${weWon ? 'text-green-700' : theyWon ? 'text-slate-400' : 'text-slate-900'}`}>{ourSets}</div>
                <div className="text-xs text-slate-500 font-medium truncate">{ownTeams.find(t => t.id === activeRecord.teamId)?.name || 'Us'}</div>
              </div>
              <div className="text-slate-400 font-bold text-xs">SETS</div>
              <div className="flex-1 text-center">
                <div className={`font-black text-xl ${theyWon ? 'text-red-600' : weWon ? 'text-slate-400' : 'text-slate-900'}`}>{theirSets}</div>
                <div className="text-xs text-slate-500 font-medium truncate">{gameState.opponent}</div>
              </div>
            </div>
          );
        })()}

        {/* Admin: set as record */}
        {isAdmin && !activeRecord.isRecord && (
          <button
            onClick={() => handleSetRecord(activeRecord)}
            disabled={settingRecord === activeRecord.id}
            className="w-full text-xs font-bold text-amber-600 border border-amber-200 bg-amber-50 hover:bg-amber-100 rounded-lg py-1.5 flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
          >
            <Star className="w-3 h-3" />
            {settingRecord === activeRecord.id ? 'Setting…' : 'Set as Match of Record'}
          </button>
        )}
        {activeRecord.isRecord && (
          <div className="text-xs text-center text-green-700 font-bold bg-green-50 border border-green-100 rounded-lg py-1">
            ★ This is the match of record
          </div>
        )}

        {/* Diff table — only when ≥2 submissions */}
        {records.length >= 2 && (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="text-xs w-full min-w-[400px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-2 py-1.5 text-slate-500 font-bold w-24">Player / Stat</th>
                  {records.map(r => (
                    <th key={r.id} className="px-2 py-1.5 text-center font-bold text-slate-700 whitespace-nowrap">
                      {r.submittedByName}{r.isRecord ? ' ★' : ''}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 text-center text-slate-500 font-bold">Match?</th>
                </tr>
              </thead>
              <tbody>
                {/* Score row */}
                {(() => {
                  const scores = records.map(r => `${r.gameState.ourSetsWon}-${r.gameState.theirSetsWon}`);
                  const allMatch = scores.every(s => s === scores[0]);
                  return (
                    <tr className={`border-b border-slate-100 ${!allMatch ? 'bg-amber-50' : ''}`}>
                      <td className="px-2 py-1 font-bold text-slate-700">Score</td>
                      {scores.map((s, i) => <td key={i} className="px-2 py-1 text-center text-slate-700">{s}</td>)}
                      <td className="px-2 py-1 text-center">{allMatch ? <span className="text-green-600">✓</span> : <span className="text-amber-600">⚠</span>}</td>
                    </tr>
                  );
                })()}
                {/* Per-player stat rows */}
                {allPlayerIds.flatMap(pid => {
                  const playerName = (() => {
                    for (const r of records) {
                      const found = r.gameState.stats.find(s => s.playerId === pid);
                      if (found) {
                        // Try to find a player name from the stat's playerId (we just use ID here — callers could enrich)
                        return pid;
                      }
                    }
                    return pid;
                  })();
                  return KEY_STATS.map(stat => {
                    const vals = allStatCounts.map(c => c[pid]?.[stat] || 0);
                    if (vals.every(v => v === 0)) return null;
                    const allMatch = vals.every(v => v === vals[0]);
                    return (
                      <tr key={`${pid}-${stat}`} className={`border-b border-slate-100 ${!allMatch ? 'bg-amber-50' : ''}`}>
                        <td className="px-2 py-1 text-slate-500 truncate max-w-[96px]">{playerName.slice(0, 8)} {STAT_LABELS[stat]}</td>
                        {vals.map((v, i) => <td key={i} className="px-2 py-1 text-center text-slate-700">{v || '—'}</td>)}
                        <td className="px-2 py-1 text-center">{allMatch ? <span className="text-green-600">✓</span> : <span className="text-amber-600">⚠</span>}</td>
                      </tr>
                    );
                  }).filter(Boolean);
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Determine how a match record was created based on its stats' source field
  const getRecordSource = (record: MatchRecord): 'live' | 'import' | 'manual' => {
    const stats = record.gameState.stats;
    if (stats.some(s => s.source === 'import')) return 'import';
    if (stats.some(s => s.source === 'manual')) return 'manual';
    return 'live';
  };

  // Determine the record's *original* source plus whether it has since been edited.
  // Edits regenerate stats with source='manual', so without `originalSource` a live
  // game that's later corrected would collapse to "Manual" — losing its Live identity.
  const getRecordProvenance = (record: MatchRecord): { source: 'live' | 'import' | 'manual'; edited: boolean } => {
    const stamped = record.gameState.originalSource;
    const derived = getRecordSource(record);
    if (stamped) {
      return { source: stamped, edited: stamped !== 'manual' && derived === 'manual' };
    }
    return { source: derived, edited: false };
  };

  const EVENT_TYPE_EMOJI: Record<string, string> = {
    game: '🏐',
    tournament: '🏆',
    practice: '🏃',
    other: '📅',
  };

  // Existing tournaments — for reuse in the form dropdown
  const existingTournaments = useMemo(() => {
    const map = new Map<string, { id: string; name: string; date: string; location: string; teamId?: string }>();
    events.forEach(e => {
      if (e.tournamentId && e.tournamentName && !map.has(e.tournamentId)) {
        map.set(e.tournamentId, { id: e.tournamentId, name: e.tournamentName, date: e.date, location: e.location, teamId: e.teamId });
      }
    });
    return Array.from(map.values());
  }, [events]);

  // Generate options for selectors
  const hours = Array.from({length: 12}, (_, i) => (i + 1).toString());
  const minutes = Array.from({length: 12}, (_, i) => (i * 5).toString().padStart(2, '0'));

  const renderEventCard = (event: GameEvent, isPast: boolean) => {
    const dateObj = robustParseDate(event.date);
    const eventRecords = matchRecords.filter(r => r.eventId === event.id);
    const hasResults = isPast && isGameType(event.type) && eventRecords.length > 0;
    const isExpanded = expandedResultsEventId === event.id;

    const cardClickHandler =
      event.type === 'practice' && onOpenPractice
        ? () => onOpenPractice(event.date)
        : isPast && isGameType(event.type)
          ? () => handleViewDetails(event)
          : isGameType(event.type) && onPlayGame
            ? () => onPlayGame(event.id)
            : undefined;

    return (
      <div
        key={event.id}
        className={`
            rounded-xl border shadow-sm group transition-all
            ${isPast
                ? 'bg-slate-50 border-slate-200 opacity-80 hover:opacity-100 hover:shadow-md'
                : isGameType(event.type) && onPlayGame
                  ? 'bg-white border-slate-200 hover:shadow-md'
                  : event.type === 'other'
                  ? 'bg-white border-amber-100 hover:shadow-md'
                  : 'bg-white border-slate-200 hover:shadow-md'
            }
        `}
      >
        {/* Row: clickable content + action buttons side-by-side */}
        <div className="flex items-stretch">
          {/* Content area — native button when navigable for reliable mobile tap handling */}
          <button
            type="button"
            onClick={cardClickHandler ?? undefined}
            className={`flex-1 p-4 flex flex-col sm:flex-row sm:items-center gap-3 min-w-0 text-left bg-transparent border-0 ${cardClickHandler ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className={`
                w-12 h-12 rounded-lg flex flex-col items-center justify-center shrink-0 border
                ${isPast
                    ? 'bg-slate-100 text-slate-400 border-slate-200 grayscale'
                    : isGameType(event.type) ? 'bg-red-50 text-red-600 border-red-100' : event.type === 'other' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-blue-50 text-blue-600 border-blue-100'
                }
              `}>
                <span className="text-[9px] font-bold uppercase leading-none">{dateObj ? formatMonth(dateObj) : '-'}</span>
                <span className="text-lg font-bold leading-none my-0.5">{dateObj ? dateObj.getDate() : '-'}</span>
                <span className="text-[9px] font-medium leading-none opacity-80">{dateObj ? dateObj.getFullYear() : ''}</span>
              </div>
              <div className="min-w-0">
                <h4 className={`font-bold text-lg truncate ${isPast ? 'text-slate-600' : 'text-slate-900'}`}>{event.title}</h4>
                <div className={`flex flex-wrap items-center text-sm gap-3 mt-1 ${isPast ? 'text-slate-400' : 'text-slate-500'}`}>
                  <span className="flex items-center gap-1 font-medium"><Clock className="w-3 h-3" /> {event.time}{event.endTime ? ` – ${event.endTime}` : ''}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {event.location}</span>
                  {event.opponent && <span className="font-medium">vs {event.opponent}</span>}
                </div>
              </div>
            </div>

            {/* Badges */}
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              {event.teamId && (() => {
                const t = ownTeams.find(t => t.id === event.teamId);
                return t ? (
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600 border border-slate-200">
                    {t.name}
                  </span>
                ) : null;
              })()}
              {hasResults && (() => {
                const bySource = { live: 0, import: 0, manual: 0 };
                const editedBySource = { live: 0, import: 0, manual: 0 };
                eventRecords.forEach(r => {
                  const { source, edited } = getRecordProvenance(r);
                  bySource[source]++;
                  if (edited) editedBySource[source]++;
                });
                const sourceCfg = {
                  live:   { label: 'Live',   classes: 'bg-green-100 text-green-700 border-green-200' },
                  import: { label: 'Import', classes: 'bg-blue-100 text-blue-700 border-blue-200' },
                  manual: { label: 'Manual', classes: 'bg-amber-100 text-amber-700 border-amber-200' },
                } as const;
                return (
                  <div className="flex items-center gap-1 flex-wrap">
                    <BarChart2 className="w-3 h-3 text-slate-400 shrink-0" />
                    {(['live', 'import', 'manual'] as const).map(src => {
                      if (bySource[src] === 0) return null;
                      const isEdited = editedBySource[src] > 0;
                      return (
                        <span key={src} className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold border ${sourceCfg[src].classes}`}>
                          {bySource[src]} {sourceCfg[src].label}
                          {isEdited && <span className="ml-1 opacity-70 font-normal italic">· edited</span>}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
              {(['practice', 'game', 'tournament', 'other'] as const).includes(event.type as any) && (
                <span className={`
                  px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide
                  ${isPast
                      ? 'bg-slate-200 text-slate-500'
                      : event.type === 'tournament' ? 'bg-amber-100 text-amber-700' : isGameType(event.type) ? 'bg-red-100 text-red-700' : event.type === 'other' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                  }
                `}>
                  {event.type === 'practice' ? 'Practice' : event.type === 'game' ? 'Game' : event.type === 'tournament' ? 'Tournament' : 'Other'}
                </span>
              )}
              {/* W/L result badge — shown inline on past game cards without needing to expand */}
              {hasResults && (() => {
                const rec = eventRecords.find(r => r.isRecord) || eventRecords[0];
                let ourSets = rec.gameState.ourSetsWon;
                let theirSets = rec.gameState.theirSetsWon;
                // Fall back to title-parsed score when stored sets are 0-0 (e.g. pre-setHistory imports)
                if (ourSets === 0 && theirSets === 0) {
                  const m = event.title?.match(/\(([WL])\s+(\d+)-(\d+)\)/i);
                  if (m) {
                    const won = m[1].toUpperCase() === 'W';
                    const a = parseInt(m[2]), b = parseInt(m[3]);
                    ourSets = won ? a : b;
                    theirSets = won ? b : a;
                  }
                }
                if (ourSets === 0 && theirSets === 0) return null;
                const won = ourSets > theirSets;
                return (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${won ? 'bg-green-100 text-green-700 border-green-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                    {won ? 'W' : 'L'} {ourSets}–{theirSets}
                  </span>
                );
              })()}
            </div>
          </button>

          {/* Action buttons — outside the clickable area so they never block card taps */}
          <div className="flex flex-col justify-center gap-1 pr-2 pl-1 border-l border-slate-100 shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); openEditModal(event); }}
              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Edit Event"
            >
              <Edit2 className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); initiateDeleteEvent(event); }}
              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete Event"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            {hasResults && (
              <button
                onClick={(e) => { e.stopPropagation(); handleViewDetails(event); }}
                className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                title={isExpanded ? 'Hide results' : 'Show results'}
              >
                {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
            )}
          </div>
        </div>
        {/* Inline match results panel */}
        {isExpanded && (
          <div className="px-4 pb-4">
            {renderMatchResultsPanel(event)}
          </div>
        )}
      </div>
    );
  };

  // Group events into tournament groups and standalone events, preserving order
  const groupEventsForDisplay = (eventList: GameEvent[], isPast: boolean) => {
    type DisplayItem = { type: 'single'; event: GameEvent } | { type: 'tournament'; tournamentId: string; tournamentName: string; location: string; events: GameEvent[]; containerEvent?: GameEvent };
    const items: DisplayItem[] = [];
    const tournamentMap = new Map<string, DisplayItem & { type: 'tournament' }>();

    eventList.forEach(event => {
      if (event.tournamentId && event.tournamentName) {
        if (!tournamentMap.has(event.tournamentId)) {
          const group: DisplayItem & { type: 'tournament' } = { type: 'tournament', tournamentId: event.tournamentId, tournamentName: event.tournamentName, location: event.location, events: [] };
          tournamentMap.set(event.tournamentId, group);
          items.push(group);
        }
        const group = tournamentMap.get(event.tournamentId)!;
        if (event.type === 'tournament') {
          // The container event seeds the group metadata but is not shown as a game row
          group.containerEvent = event;
        } else {
          group.events.push(event);
        }
      } else if (event.type !== 'tournament') {
        items.push({ type: 'single', event });
      }
    });
    return items;
  };

  const renderTournamentCard = (group: { tournamentId: string; tournamentName: string; location: string; events: GameEvent[]; containerEvent?: GameEvent }, isPast: boolean) => {
    const dates = group.events.map(e => robustParseDate(e.date)).filter(Boolean) as Date[];
    const minDate = dates.length > 0 ? new Date(Math.min(...dates.map(d => d.getTime()))) : null;
    const maxDate = dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : null;
    const dateLabel = minDate && maxDate
      ? minDate.getTime() === maxDate.getTime()
        ? formatDate(minDate, { month: 'short', day: 'numeric', year: 'numeric' })
        : `${formatMonthDay(minDate)} – ${formatDate(maxDate, { month: 'short', day: 'numeric', year: 'numeric' })}`
      : '';

    return (
      <div key={group.tournamentId} className={`rounded-xl border shadow-sm overflow-hidden ${isPast ? 'bg-slate-50 border-slate-200 opacity-80 hover:opacity-100' : 'bg-white border-amber-200'}`}>
        {/* Tournament Header */}
        <div className="flex items-center gap-3 p-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isPast ? 'bg-slate-100 text-slate-400' : 'bg-amber-100 text-amber-600'}`}>
            <Trophy className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className={`font-bold text-lg ${isPast ? 'text-slate-600' : 'text-slate-900'}`}>{group.tournamentName}</h4>
            <div className={`flex items-center text-sm gap-3 mt-0.5 ${isPast ? 'text-slate-400' : 'text-slate-500'}`}>
              {dateLabel && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {dateLabel}</span>}
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {group.location}</span>
              <span className="font-medium">{group.events.length} game{group.events.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirmation({ id: '', title: group.tournamentName, tournamentId: group.tournamentId });
            }}
            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete tournament"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        {/* Tournament Games — always visible */}
        <div className="border-t border-slate-100">
            {group.events.length === 0 && !isPast && (
              <div className="flex flex-col items-center justify-center px-4 py-5 gap-2">
                <p className="text-sm text-slate-400">No games added yet</p>
                <button
                  onClick={() => {
                    const ref = group.containerEvent;
                    openAddModal({
                      type: 'game',
                      tournamentId: group.tournamentId,
                      tournamentName: group.tournamentName,
                      date: ref?.date,
                      location: ref?.location || group.location,
                      teamId: ref?.teamId,
                      title: `${group.tournamentName} Game`,
                    });
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" /> Add First Game
                </button>
              </div>
            )}
            {group.events.map(event => {
              const eventRecords = matchRecords.filter(r => r.eventId === event.id);
              const record = eventRecords.find(r => r.isRecord) || eventRecords[0];
              // Resolve sets with title fallback for pre-setHistory imports
              let tOurSets = record?.gameState.ourSetsWon ?? 0;
              let tTheirSets = record?.gameState.theirSetsWon ?? 0;
              if (record && tOurSets === 0 && tTheirSets === 0) {
                const m = event.title?.match(/\(([WL])\s+(\d+)-(\d+)\)/i);
                if (m) {
                  const won = m[1].toUpperCase() === 'W';
                  const a = parseInt(m[2]), b = parseInt(m[3]);
                  tOurSets = won ? a : b;
                  tTheirSets = won ? b : a;
                }
              }
              const hasScore = record && (tOurSets > 0 || tTheirSets > 0);
              const tWon = tOurSets > tTheirSets;

              return (
                <div
                  key={event.id}
                  onClick={
                    isPast
                      ? () => handleViewDetails(event)
                      : (isGameType(event.type) && onPlayGame)
                        ? () => onPlayGame(event.id)
                        : undefined
                  }
                  className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-50 last:border-b-0 group/row hover:bg-slate-50 ${isPast || (isGameType(event.type) && onPlayGame) ? 'cursor-pointer' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-bold ${isPast ? 'text-slate-500' : 'text-slate-800'}`}>
                        {event.opponent ? `vs ${event.opponent}` : event.title}
                      </span>
                      {hasScore && (
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${tWon ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {tWon ? 'W' : 'L'} {tOurSets}–{tTheirSets}
                        </span>
                      )}
                    </div>
                    <span className={`text-xs ${isPast ? 'text-slate-400' : 'text-slate-500'}`}>
                      <Clock className="w-3 h-3 inline mr-1" />{event.time}
                      {!event.opponent && event.title && <span className="ml-2">{event.title}</span>}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); openEditModal(event); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={(e) => { e.stopPropagation(); initiateDeleteEvent(event); }} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              );
            })}
            {/* + Add Game to Tournament */}
            {!isPast && group.events.length > 0 && (
              <button
                onClick={() => {
                  const ref = group.events[0] || group.containerEvent;
                  openAddModal({
                    type: 'game',
                    tournamentId: group.tournamentId,
                    tournamentName: group.tournamentName,
                    date: ref?.date,
                    location: ref?.location || group.location,
                    teamId: ref?.teamId,
                    title: `${group.tournamentName} Game`,
                  });
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-amber-600 hover:bg-amber-50 transition-colors border-t border-slate-100"
              >
                <Plus className="w-4 h-4" /> Add Game
              </button>
            )}
          </div>
      </div>
    );
  };

  const renderEventList = (eventList: GameEvent[], isPast: boolean) => {
    const items = groupEventsForDisplay(eventList, isPast);
    return items.map(item => {
      if (item.type === 'tournament') return renderTournamentCard(item, isPast);
      return renderEventCard(item.event, isPast);
    });
  };

  return (
    <div className="space-y-6">
      {/* DELETE CONFIRMATION MODAL */}
      {deleteConfirmation && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm animate-fade-in-up">
                <div className="flex flex-col items-center text-center mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3 text-red-600">
                        <AlertTriangle className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg text-slate-900">{deleteConfirmation.tournamentId ? 'Delete Tournament?' : 'Delete Event?'}</h3>
                    <p className="text-slate-500 mt-1">
                        {deleteConfirmation.tournamentId
                          ? <>Are you sure you want to delete <strong className="text-slate-900">{deleteConfirmation.title}</strong> and all its games? This cannot be undone.</>
                          : <>Are you sure you want to delete <strong className="text-slate-900">{deleteConfirmation.title}</strong>? This cannot be undone.</>
                        }
                    </p>
                </div>
                <div className="flex gap-3">
                    <button 
                        onClick={() => setDeleteConfirmation(null)}
                        className="flex-1 py-2.5 text-slate-600 font-bold hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={executeDeleteEvent}
                        className="flex-1 py-2.5 bg-red-600 text-white font-bold hover:bg-red-700 rounded-lg shadow-sm transition-colors"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-start gap-3 flex-wrap flex-1">
           <div>
             <h2 className="text-2xl font-bold text-slate-900">Schedule</h2>
             <p className="text-slate-500 mt-1">Upcoming games and practices.</p>
           </div>
           {seasons.length > 0 && (
             <div className="mt-1.5">
               <SeasonSelector seasons={seasons} viewingSeason={viewingSeason} onChange={setViewingSeason} />
             </div>
           )}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {ownTeams.length > 1 ? (
            <div className="flex flex-col gap-1">
              <div className="relative flex items-center">
                <Filter className="w-3.5 h-3.5 absolute left-2.5 text-slate-400 pointer-events-none" />
                <select
                  className="pl-8 pr-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-700 text-sm font-medium appearance-none cursor-pointer"
                  value={filterTeamId}
                  onChange={e => setFilterTeamId(e.target.value)}
                >
                  <option value="">All Teams</option>
                  {ownTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              {(teamRecord.wins > 0 || teamRecord.losses > 0) && (
                <div className="flex items-center gap-1.5 px-1">
                  <Trophy className="w-3 h-3 text-amber-500" />
                  <span className="text-xs font-bold text-slate-600">{teamRecord.wins}–{teamRecord.losses}</span>
                  <span className="text-[10px] text-slate-400">this season</span>
                </div>
              )}
            </div>
          ) : (teamRecord.wins > 0 || teamRecord.losses > 0) ? (
            <div className="flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2">
              <Trophy className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-sm font-bold text-slate-700">{teamRecord.wins}–{teamRecord.losses}</span>
              <span className="text-xs text-slate-400">this season</span>
            </div>
          ) : null}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isExtracting}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 transition-colors"
          >
            {isExtracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            <span>{isExtracting ? 'Analyzing...' : 'Scan Schedule'}</span>
          </button>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*,application/pdf"
            onChange={handleFileUpload}
          />
          <button
            onClick={openAddModal}
            className="bg-slate-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-slate-800 flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> Manual Add
          </button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-lg animate-fade-in-up w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">{editingId ? 'Edit Event' : 'New Event'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5"/></button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Event Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Event Type</label>
                <select
                  className="w-full p-2 border rounded-lg bg-white text-slate-900"
                  value={newEvent.type}
                  onChange={e => {
                    const val = e.target.value as GameEvent['type'];
                    setNewEvent({...newEvent, type: val, title: ''});
                    if (val === 'tournament') {
                      setIsTournament(true);
                    } else if (val !== 'game') {
                      setIsTournament(false);
                      setTournamentName('');
                      setTournamentId('');
                    }
                    setFormErrors({});
                  }}
                >
                  <option value="practice">Practice</option>
                  <option value="game">Game</option>
                  <option value="tournament">Tournament</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Title — always visible; optional for practice/game, required for tournament/other */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {newEvent.type === 'tournament' ? 'Tournament Name' : 'Title'}
                  {(newEvent.type === 'tournament' || newEvent.type === 'other')
                    ? <span className="text-red-500"> *</span>
                    : <span className="text-slate-400 font-normal"> (optional)</span>
                  }
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg bg-white text-slate-900 placeholder:text-slate-400 ${formErrors.title ? 'border-red-400' : ''}`}
                  placeholder={
                    newEvent.type === 'tournament' ? 'e.g. Fall Classic' :
                    newEvent.type === 'other' ? 'e.g. Team Meeting' :
                    newEvent.type === 'game'
                      ? (ownTeams.find(t => t.id === newEvent.teamId)?.name
                          ? `e.g. ${ownTeams.find(t => t.id === newEvent.teamId)!.name} Game`
                          : 'e.g. Varsity Game')
                      : (ownTeams.find(t => t.id === newEvent.teamId)?.name
                          ? `e.g. ${ownTeams.find(t => t.id === newEvent.teamId)!.name} Practice`
                          : 'e.g. Varsity Practice')
                  }
                  value={newEvent.title || ''}
                  onChange={e => { setNewEvent({...newEvent, title: e.target.value}); if (formErrors.title) setFormErrors(p => ({...p, title: ''})); }}
                />
                {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
              </div>

              {/* GAME: Tournament checkbox — shown at top */}
              {newEvent.type === 'game' && (
                <div className="md:col-span-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="tournamentCheck"
                      className="cursor-pointer"
                      checked={isTournament}
                      onChange={e => {
                        setIsTournament(e.target.checked);
                        if (!e.target.checked) { setTournamentName(''); setTournamentId(''); }
                      }}
                    />
                    <label htmlFor="tournamentCheck" className="text-sm font-medium text-slate-700 cursor-pointer flex items-center gap-1.5">
                      <Trophy className="w-4 h-4 text-amber-500" /> Part of a tournament
                    </label>
                  </div>
                  {isTournament && (
                    <div className="mt-2 space-y-2 pl-6">
                      {existingTournaments.length > 0 && (
                        <select
                          className="w-full p-2 border rounded-lg bg-white text-slate-900 text-sm"
                          value={tournamentId || '__new__'}
                          onChange={e => {
                            if (e.target.value === '__new__') {
                              setTournamentId('');
                              setTournamentName('');
                            } else {
                              const t = existingTournaments.find(t => t.id === e.target.value);
                              if (t) {
                                setTournamentId(t.id);
                                setTournamentName(t.name);
                                if (!newEvent.location || newEvent.location === 'TBD') setNewEvent(prev => ({ ...prev, location: t.location, date: t.date, ...(t.teamId ? { teamId: t.teamId } : {}) }));
                                else setNewEvent(prev => ({ ...prev, date: t.date, ...(t.teamId ? { teamId: t.teamId } : {}) }));
                              }
                            }
                          }}
                        >
                          <option value="__new__">+ New Tournament</option>
                          {existingTournaments.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      )}
                      {!tournamentId && (
                        <input
                          type="text"
                          className="w-full p-2 border rounded-lg bg-white text-slate-900 placeholder:text-slate-400 text-sm"
                          placeholder="Tournament name (e.g. Fall Classic)"
                          value={tournamentName}
                          onChange={e => setTournamentName(e.target.value)}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* GAME: Opponent (required) */}
              {newEvent.type === 'game' && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Opponent <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`w-full p-2 border rounded-lg bg-white text-slate-900 placeholder:text-slate-400 ${formErrors.opponent ? 'border-red-400' : ''}`}
                    placeholder="e.g. North High"
                    value={newEvent.opponent || ''}
                    onChange={e => { setNewEvent({...newEvent, opponent: e.target.value}); if (formErrors.opponent) setFormErrors(p => ({...p, opponent: ''})); }}
                  />
                  {formErrors.opponent && <p className="text-red-500 text-xs mt-1">{formErrors.opponent}</p>}
                </div>
              )}


              {/* Team */}
              {ownTeams.length > 0 && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Team (Optional)</label>
                  <select
                    className="w-full p-2 border rounded-lg bg-white text-slate-900"
                    value={newEvent.teamId || ''}
                    onChange={e => setNewEvent({...newEvent, teamId: e.target.value || undefined})}
                  >
                    <option value="">All Teams</option>
                    {ownTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              )}

              {/* Date (required) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className={`w-full p-2 border rounded-lg bg-white text-slate-900 cursor-pointer ${formErrors.date ? 'border-red-400' : ''}`}
                  value={newEvent.date || ''}
                  onChange={e => { setNewEvent({...newEvent, date: e.target.value}); if (formErrors.date) setFormErrors(p => ({...p, date: ''})); }}
                />
                {formErrors.date && <p className="text-red-500 text-xs mt-1">{formErrors.date}</p>}
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Time</label>
                <div className="flex items-center gap-1">
                  {!isTbdTime ? (
                    <>
                      <div className="relative flex-1">
                        <select
                          className="w-full p-2 border rounded-lg bg-white text-slate-900 appearance-none text-center cursor-pointer"
                          value={timeHour}
                          onChange={(e) => setTimeHour(e.target.value)}
                        >
                          {hours.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                      <span className="font-bold">:</span>
                      <div className="relative flex-1">
                        <select
                          className="w-full p-2 border rounded-lg bg-white text-slate-900 appearance-none text-center cursor-pointer"
                          value={timeMinute}
                          onChange={(e) => setTimeMinute(e.target.value)}
                        >
                          {minutes.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </div>
                      <div className="relative flex-1">
                        <select
                          className="w-full p-2 border rounded-lg bg-white text-slate-900 appearance-none text-center cursor-pointer"
                          value={timeAmPm}
                          onChange={(e) => setTimeAmPm(e.target.value)}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 p-2 bg-slate-100 rounded-lg text-slate-500 text-center font-medium border border-slate-200">
                      Time TBD
                    </div>
                  )}
                </div>
                <div className="mt-1 flex items-center">
                  <input
                    type="checkbox"
                    id="tbdCheck"
                    className="mr-2 cursor-pointer"
                    checked={isTbdTime}
                    onChange={(e) => setIsTbdTime(e.target.checked)}
                  />
                  <label htmlFor="tbdCheck" className="text-xs text-slate-500 cursor-pointer">Set time as TBD</label>
                </div>
              </div>

              {/* End Time (optional) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Time <span className="text-slate-400 font-normal">(optional)</span></label>
                {hasEndTime ? (
                  <div className="flex items-center gap-1">
                    <div className="relative flex-1">
                      <select
                        className="w-full p-2 border rounded-lg bg-white text-slate-900 appearance-none text-center cursor-pointer"
                        value={endTimeHour}
                        onChange={e => setEndTimeHour(e.target.value)}
                      >
                        {hours.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                    <span className="font-bold">:</span>
                    <div className="relative flex-1">
                      <select
                        className="w-full p-2 border rounded-lg bg-white text-slate-900 appearance-none text-center cursor-pointer"
                        value={endTimeMinute}
                        onChange={e => setEndTimeMinute(e.target.value)}
                      >
                        {minutes.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="relative flex-1">
                      <select
                        className="w-full p-2 border rounded-lg bg-white text-slate-900 appearance-none text-center cursor-pointer"
                        value={endTimeAmPm}
                        onChange={e => setEndTimeAmPm(e.target.value)}
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                    <button type="button" onClick={() => setHasEndTime(false)} className="p-1 text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setHasEndTime(true)}
                    className="w-full p-2 border border-dashed border-slate-300 rounded-lg text-slate-400 text-sm hover:border-slate-400 hover:text-slate-600 transition-colors"
                  >
                    + Add end time
                  </button>
                )}
              </div>

              {/* Location */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-lg bg-white text-slate-900 placeholder:text-slate-400"
                  placeholder="e.g. Main Gym, Court 1"
                  value={newEvent.location || ''}
                  onChange={e => setNewEvent({...newEvent, location: e.target.value})}
                />
              </div>


            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button 
                onClick={() => setIsModalOpen(false)}
                className="text-slate-600 hover:bg-slate-100 px-4 py-2 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveEvent}
                className="bg-slate-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-slate-800"
              >
                {editingId ? 'Update Event' : 'Save Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Scanned Events Modal (Unchanged) */}
      {isReviewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-4xl max-h-[90vh] flex flex-col animate-fade-in-up">
            <div className="flex justify-between items-center mb-6 border-b pb-4">
              <div>
                <h3 className="font-bold text-xl text-slate-900">Review Scanned Events</h3>
                <p className="text-slate-500 text-sm">Please review and edit the extracted schedule before importing.</p>
              </div>
              <button onClick={() => { setIsReviewing(false); setScannedEvents([]); }} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6"/></button>
            </div>
            
            <div className="overflow-y-auto flex-1 space-y-4 pr-2">
               {scannedEvents.map((evt) => {
                 const isGame = evt.type === 'game' || evt.type === 'tournament';
                 const missingOpponent = isGame && !evt.opponent?.trim();
                 return (
                 <div key={evt.id} className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    <div className="md:col-span-2">
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Type</label>
                       <select
                          className="w-full p-2 border rounded-lg text-sm bg-white"
                          value={evt.type}
                          onChange={(e) => updateScannedEvent(evt.id!, 'type', e.target.value)}
                        >
                          <option value="practice">Practice</option>
                          <option value="game">Game</option>
                          <option value="tournament">Tournament</option>
                          <option value="other">Other</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Title <span className="text-slate-400 normal-case font-normal">(optional)</span></label>
                       <input
                         className="w-full p-2 border rounded-lg text-sm bg-white text-slate-900"
                         value={evt.title || ''}
                         onChange={(e) => updateScannedEvent(evt.id!, 'title', e.target.value)}
                         placeholder="Event Title"
                       />
                    </div>
                    <div className="md:col-span-2">
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                         Opponent {isGame && <span className="text-red-500">*</span>}
                       </label>
                       <input
                         className={`w-full p-2 border rounded-lg text-sm bg-white text-slate-900 ${missingOpponent ? 'border-red-400 bg-red-50' : ''} ${!isGame ? 'opacity-40 cursor-not-allowed' : ''}`}
                         value={evt.opponent || ''}
                         onChange={(e) => updateScannedEvent(evt.id!, 'opponent', e.target.value)}
                         placeholder={isGame ? 'Required' : '—'}
                         disabled={!isGame}
                       />
                    </div>
                    <div className="md:col-span-2">
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Date</label>
                       <input
                         type="date"
                         className="w-full p-2 border rounded-lg text-sm bg-white text-slate-900"
                         value={evt.date || ''}
                         onChange={(e) => updateScannedEvent(evt.id!, 'date', e.target.value)}
                       />
                    </div>
                     <div className="md:col-span-1">
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Time</label>
                       <input
                         type="time"
                         className="w-full p-2 border rounded-lg text-sm bg-white text-slate-900"
                         value={evt.time || ''}
                         onChange={(e) => updateScannedEvent(evt.id!, 'time', e.target.value)}
                         />
                    </div>
                    <div className="md:col-span-2">
                       <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Location</label>
                       <input
                         className="w-full p-2 border rounded-lg text-sm bg-white text-slate-900"
                         value={evt.location || ''}
                         onChange={(e) => updateScannedEvent(evt.id!, 'location', e.target.value)}
                         placeholder="Location"
                       />
                    </div>
                    <div className="md:col-span-1 flex justify-center pb-2">
                       <button
                        onClick={() => removeScannedEvent(evt.id!)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-full transition-colors"
                        title="Remove Event"
                       >
                         <Trash2 className="w-5 h-5" />
                       </button>
                    </div>
                 </div>
                 );
               })}
               {scannedEvents.length === 0 && (
                 <div className="text-center py-8 text-slate-400">All events removed.</div>
               )}
            </div>

            <div className="border-t pt-4 mt-4 flex justify-end gap-3">
               <button 
                  onClick={() => { setIsReviewing(false); setScannedEvents([]); }}
                  className="px-6 py-2 rounded-lg text-slate-600 font-medium hover:bg-slate-100 transition-colors"
               >
                 Cancel
               </button>
               <button
                  onClick={commitScannedEvents}
                  disabled={scannedEvents.length === 0 || scannedEvents.some(evt => (evt.type === 'game' || evt.type === 'tournament') && !evt.opponent?.trim())}
                  className="bg-indigo-600 text-white px-8 py-2 rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
               >
                 <Check className="w-4 h-4" />
                 Import {scannedEvents.length} Events
               </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Events List */}
      <div className="space-y-8">
        
        {/* Upcoming Section */}
        <div>
            <h3 className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600"/> Upcoming
            </h3>
            <div className="space-y-4">
                {upcomingEvents.length === 0 ? (
                    <div className="text-center py-8 bg-slate-50 rounded-xl text-slate-400 border border-slate-100">
                        No upcoming events scheduled.
                    </div>
                ) : (
                    renderEventList(upcomingEvents, false)
                )}
            </div>
        </div>

        {/* Past Section */}
        {pastEvents.length > 0 && (
            <div>
                <h3 className="font-bold text-lg text-slate-500 mb-4 flex items-center gap-2 pt-4 border-t border-slate-100">
                    <History className="w-5 h-5"/> Past Events
                </h3>
                <div className="space-y-4">
                    {renderEventList(pastEvents, true)}
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Schedule;
